import {announce} from '@github-ui/aria-live'
import {boardCellKey, type CellAddress} from '@github-ui/board-core'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useRef, useState} from 'react'

import {appendCellPage} from './append-cell-page'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from './types'

const NO_CELLS: ReadonlySet<string> = new Set()

// The cell announced that it was loading, so a skeleton that simply disappears leaves a reader
// who cannot see it waiting on a page that is never coming.
const PAGE_FAILED = 'Could not load more cards. Try again.'

/**
 * Neither half alone is ownership: a board can be left and returned to, and one cell can be
 * asked twice from the same cursor after a failure. Only whoever is recorded here now may
 * append or clear the cell's loading state.
 */
type CellRequest = {boardId: string; requestId: number; controller: AbortController}

type BoardCellPaging = {
  /** Undefined where the domain cannot page a cell, which leaves its sentinels unobserved. */
  onLoadMoreItems?: (address: CellAddress) => void
  /** Keyed by board-core cell key. */
  loadingCells: ReadonlySet<string>
}

/**
 * Asks for one cell's next page and folds it into the board already on screen.
 *
 * Loading is held here rather than in the cache, which would hand a stuck spinner to the next
 * reader of the same board. Nothing retries: a failed page leaves the cell as it was, so
 * board-core's sentinel asks again on the reader's next settle.
 *
 * Two windows can still disagree — a relative date crossing its boundary between requests, and
 * a saved view edited while its board is on screen. Both want the effective query bound the way
 * the axes already are (github/views#177 follow-up).
 */
export function useBoardCellPaging<TItem extends SavedViewsBoardItem, TMetadata>({
  adapter,
  options,
  queryKey,
}: {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata>
  options: SavedViewsBoardOptions
  queryKey: QueryKey
}): BoardCellPaging {
  const queryClient = useQueryClient()
  const [loadingCells, setLoadingCells] = useState<ReadonlySet<string>>(NO_CELLS)
  const requestsRef = useRef(new Map<string, CellRequest>())
  const lastRequestIdRef = useRef(0)

  const boardId = hashKey(queryKey)
  const {fetchCellPage} = adapter
  const {query} = options

  // Abandoning a board abandons its requests: their answers describe a grid that is no longer
  // drawn, and a cell left recorded as loading would still be loading when the board returns.
  useEffect(() => {
    const outstanding = requestsRef.current
    return () => {
      for (const request of outstanding.values()) request.controller.abort()
      outstanding.clear()
      setLoadingCells(NO_CELLS)
    }
  }, [boardId])

  const onLoadMoreItems = useCallback(
    (address: CellAddress) => {
      if (!fetchCellPage) return

      const cellKey = boardCellKey(address)
      // board-core holds a second ask while the cell states it is loading, but this runs before
      // that state has rendered, so this covers the gap and a host that never threads loading.
      if (requestsRef.current.has(cellKey)) return

      const payload = queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey)
      const cursor = payload?.boardData.cellCursors?.get(cellKey)
      // Nothing to resume: the cell is finished, or its cards were never fetched at all.
      if (cursor == null) return

      const requestId = (lastRequestIdRef.current += 1)
      const controller = new AbortController()
      const owns = () => {
        const current = requestsRef.current.get(cellKey)
        return current?.boardId === boardId && current.requestId === requestId
      }

      requestsRef.current.set(cellKey, {boardId, requestId, controller})
      setLoadingCells(previous => new Set(previous).add(cellKey))

      // Released after the try rather than in a `finally`, which React Compiler cannot lower.
      const load = async () => {
        try {
          const page = await fetchCellPage({...address, cursor, query, signal: controller.signal})

          if (owns()) {
            queryClient.setQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey, held => {
              if (!held) return undefined
              // Ownership cannot say the board under the key is still the one the cursor was read
              // from. Nothing replaces a board in place today, so this supplements that check.
              if (held.boardData.cellCursors?.get(cellKey) !== cursor) return undefined

              const next = appendCellPage(held, address, page)
              // `undefined` is the only answer the cache treats as no write, and `appendCellPage`
              // answers by reference only when the cell is no longer drawn. A page that added no
              // card still writes, since it can advance that cell's cursor or finish it.
              return next === held ? undefined : next
            })
          }
        } catch {
          // Retrying is left to the sentinel, which asks again on the reader's next settle
          // because the cursor did not advance. An abort is the board being left, not a failure.
          if (!controller.signal.aborted) announce(PAGE_FAILED)
        }

        // Guarded, so a request the board has moved past cannot clear the loading state a newer
        // one set, or delete its record.
        if (owns()) {
          requestsRef.current.delete(cellKey)
          setLoadingCells(previous => {
            const remaining = new Set(previous)
            remaining.delete(cellKey)
            return remaining
          })
        }
      }

      void load()
    },
    [boardId, fetchCellPage, query, queryClient, queryKey],
  )

  return {onLoadMoreItems: fetchCellPage ? onLoadMoreItems : undefined, loadingCells}
}

/** The board as drawn, with the cells that have a request outstanding reporting it. */
export function withLoadingCells<TItem extends SavedViewsBoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  loadingCells: ReadonlySet<string>,
): SavedViewsBoardData<TItem> {
  if (loadingCells.size === 0) return boardData

  return {
    ...boardData,
    data: {
      ...boardData.data,
      cells: boardData.data.cells.map(cell =>
        loadingCells.has(boardCellKey(cell)) ? {...cell, isLoadingItems: true} : cell,
      ),
    },
  }
}
